import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const OrganizerSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    hotline: '',
    re_entry_restriction: false,
    re_entry_grace_minutes: 0,
    // timezone: 'UTC',
    // currency: 'USD',
  });
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    push_notifications: true,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [bankDetails, setBankDetails] = useState([]);
  const [gateway, setGateway] = useState({
    gateway: 'sadad',
    merchant_id: '',
    api_key: '',
    api_secret: '',
    environment: 'sandbox',
    configured: false,
  });
  const [isSavingGateway, setIsSavingGateway] = useState(false);
  const [isLoadingGateway, setIsLoadingGateway] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [newBankDetail, setNewBankDetail] = useState({
      account_name: '',
    iban: '',
    bank_account_number: '',
    bank_account_currency: 'QAR',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadProfile();
    if (activeTab === 'bank-details') {
      loadBankDetails();
    }
    if (activeTab === 'payment-gateway') {
      loadPaymentGateway();
    }
  }, [activeTab]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/organizer/settings/profile');
      if (response.data.status === 'success') {
        setProfile({
          name: response.data.data.name || '',
          email: response.data.data.email || '',
          phone: response.data.data.phone || '',
          hotline: response.data.data.hotline || '',
          re_entry_restriction: response.data.data.re_entry_restriction ?? false,
          re_entry_grace_minutes: response.data.data.re_entry_grace_minutes ?? 0,
          // timezone: response.data.data.timezone || 'UTC',
          // currency: response.data.data.currency || 'USD',
        });
        setNotifications({
          email_notifications: response.data.data.email_notifications,
          push_notifications: response.data.data.push_notifications,
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/profile', profile);
      if (response.data.status === 'success') {
        setSuccess('Profile updated successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/notifications', notifications);
      if (response.data.status === 'success') {
        setSuccess('Notification preferences updated successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setError('New passwords do not match');
      return;
    }

    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/password', passwordData);
      if (response.data.status === 'success') {
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const loadBankDetails = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/organizer/settings/bank-details');
      if (response.data.status === 'success') {
        setBankDetails(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading bank details:', error);
      setError('Failed to load bank details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBankDetail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.post('/organizer/settings/bank-details', newBankDetail);
      if (response.data.status === 'success') {
        setSuccess('Bank detail added successfully');
        setShowBankModal(false);
        setNewBankDetail({
            account_name: '',
          iban: '',
          bank_account_number: '',
          bank_account_currency: 'QAR',
        });
        loadBankDetails();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add bank detail');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBankDetail = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank detail?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.delete(`/organizer/settings/bank-details/${id}`);
      if (response.data.status === 'success') {
        setSuccess('Bank detail deleted successfully');
        loadBankDetails();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete bank detail');
    }
  };

  const loadPaymentGateway = async () => {
    try {
      setIsLoadingGateway(true);
      const response = await api.get('/organizer/settings/payment-gateway');
      if (response.data.status === 'success' && response.data.data) {
        setGateway({
          gateway: response.data.data.gateway || 'sadad',
          merchant_id: response.data.data.merchant_id || '',
          api_key: response.data.data.api_key || '',
          api_secret: response.data.data.api_secret || '',
          environment: response.data.data.environment || 'sandbox',
          configured: response.data.data.configured || false,
        });
      }
    } catch (err) {
      console.error('Error loading payment gateway:', err);
    } finally {
      setIsLoadingGateway(false);
    }
  };

  const handleGatewaySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSavingGateway(true);
    try {
      const response = await api.post('/organizer/settings/payment-gateway', {
        gateway: gateway.gateway,
        merchant_id: gateway.merchant_id,
        api_key: gateway.api_key,
        api_secret: gateway.api_secret,
        environment: gateway.environment,
      });
      if (response.data.status === 'success') {
        setSuccess('Payment gateway saved successfully');
        loadPaymentGateway();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save payment gateway');
    } finally {
      setIsSavingGateway(false);
    }
  };

  const handleGatewayDelete = async () => {
    if (!window.confirm('Remove SADAD payment gateway configuration? Payments will fall back to the platform default.')) return;
    setError('');
    setSuccess('');
    try {
      await api.delete('/organizer/settings/payment-gateway');
      setSuccess('Payment gateway configuration removed');
      setGateway({ gateway: 'sadad', merchant_id: '', api_key: '', api_secret: '', environment: 'sandbox', configured: false });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove gateway');
    }
  };

  const timezones = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Tokyo',
    'Australia/Sydney', 'Pacific/Auckland'
  ];

  const currencies = ['USD', 'EUR', 'GBP', 'AED', 'JPY', 'AUD'];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="text-gray-900 h5 font-weight-bold">Settings</h5>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <i className="fas fa-user mr-2"></i>Profile
          </button>
        </li>
        {/*<li className="nav-item">*/}
        {/*  <button*/}
        {/*    className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}*/}
        {/*    onClick={() => setActiveTab('notifications')}*/}
        {/*  >*/}
        {/*    <i className="fas fa-bell mr-2"></i>Notifications*/}
        {/*  </button>*/}
        {/*</li>*/}
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <i className="fas fa-lock mr-2"></i>Change Password
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'bank-details' ? 'active' : ''}`}
            onClick={() => setActiveTab('bank-details')}
          >
            <i className="fas fa-university mr-2"></i>Bank Details
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'payment-gateway' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment-gateway')}
          >
            <i className="fas fa-credit-card mr-2"></i>Payment Gateway
          </button>
        </li>
      </ul>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Profile Information</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleProfileSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          value={profile.name}
                          onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={profile.email}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="phone">Phone</label>
                            <input
                              type="text"
                              className="form-control"
                              id="phone"
                              value={profile.phone}
                              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="hotline">Hotline</label>
                            <input
                              type="text"
                              className="form-control"
                              id="hotline"
                              value={profile.hotline}
                              onChange={(e) => setProfile({ ...profile, hotline: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/*<div className="row">*/}
                      {/*  <div className="col-md-6">*/}
                      {/*    <div className="form-group">*/}
                      {/*      <label htmlFor="timezone">Timezone</label>*/}
                      {/*      <select*/}
                      {/*        className="form-control"*/}
                      {/*        id="timezone"*/}
                      {/*        value={profile.timezone}*/}
                      {/*        onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}*/}
                      {/*      >*/}
                      {/*        {timezones.map((tz) => (*/}
                      {/*          <option key={tz} value={tz}>*/}
                      {/*            {tz}*/}
                      {/*          </option>*/}
                      {/*        ))}*/}
                      {/*      </select>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*  <div className="col-md-6">*/}
                      {/*    <div className="form-group">*/}
                      {/*      <label htmlFor="currency">Currency</label>*/}
                      {/*      <select*/}
                      {/*        className="form-control"*/}
                      {/*        id="currency"*/}
                      {/*        value={profile.currency}*/}
                      {/*        onChange={(e) => setProfile({ ...profile, currency: e.target.value })}*/}
                      {/*      >*/}
                      {/*        {currencies.map((curr) => (*/}
                      {/*          <option key={curr} value={curr}>*/}
                      {/*            {curr}*/}
                      {/*          </option>*/}
                      {/*        ))}*/}
                      {/*      </select>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*</div>*/}

                      {/* Re-entry Restriction */}
                      <div className="form-group mb-4">
                        <div className="p-3 border rounded bg-light">
                          {/* Toggle row */}
                          <div className="d-flex align-items-start gap-3">
                            <div className="flex-1">
                              <label className="font-weight-bold text-gray-800 mb-0" htmlFor="re_entry_restriction">
                                Re-entry Restriction
                              </label>
                              <p className="text-muted small mb-0 mt-1">
                                When enabled, attendees who have checked out cannot re-enter the venue.
                                You can optionally allow re-entry within a grace period after exit.
                              </p>
                            </div>
                            <div className="custom-control custom-switch mt-1">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="re_entry_restriction"
                                checked={!!profile.re_entry_restriction}
                                onChange={(e) => setProfile({
                                  ...profile,
                                  re_entry_restriction: e.target.checked,
                                  re_entry_grace_minutes: e.target.checked ? profile.re_entry_grace_minutes : 0,
                                })}
                              />
                              <label className="custom-control-label" htmlFor="re_entry_restriction" />
                            </div>
                          </div>

                          {/* Grace period — only shown when restriction is on */}
                          {profile.re_entry_restriction && (
                            <div className="mt-3 pt-3 border-top">
                              <label className="font-weight-bold text-gray-700 small mb-1" htmlFor="re_entry_grace_minutes">
                                Grace Period After Exit
                              </label>
                              <p className="text-muted small mb-2">
                                Attendees can re-enter within this many minutes after checking out.
                                Set to <strong>0</strong> to block re-entry immediately.
                              </p>
                              <div className="d-flex align-items-center gap-2" style={{ maxWidth: 220 }}>
                                <input
                                  type="number"
                                  className="form-control"
                                  id="re_entry_grace_minutes"
                                  min="0"
                                  max="1440"
                                  value={profile.re_entry_grace_minutes}
                                  onChange={(e) => setProfile({
                                    ...profile,
                                    re_entry_grace_minutes: Math.max(0, Math.min(1440, parseInt(e.target.value, 10) || 0)),
                                  })}
                                />
                                <span className="text-muted small text-nowrap">minutes</span>
                              </div>
                              {profile.re_entry_grace_minutes === 0 && (
                                <small className="text-danger mt-1 d-block">
                                  Re-entry blocked immediately after exit.
                                </small>
                              )}
                              {profile.re_entry_grace_minutes > 0 && (
                                <small className="text-success mt-1 d-block">
                                  Re-entry allowed up to {profile.re_entry_grace_minutes} minute{profile.re_entry_grace_minutes !== 1 ? 's' : ''} after exit.
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <button type="submit" className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Profile'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {/*{activeTab === 'notifications' && (*/}
          {/*  <div className="row mb-4">*/}
          {/*    <div className="col-md-6">*/}
          {/*      <div className="card">*/}
          {/*        <div className="card-header">*/}
          {/*          <h5 className="mb-0">Notification Preferences</h5>*/}
          {/*        </div>*/}
          {/*        <div className="card-body">*/}
          {/*          <form onSubmit={handleNotificationsSubmit}>*/}
          {/*            <div className="form-group">*/}
          {/*              <div className="custom-control custom-switch">*/}
          {/*                <input*/}
          {/*                  type="checkbox"*/}
          {/*                  className="custom-control-input"*/}
          {/*                  id="emailNotifications"*/}
          {/*                  checked={notifications.email_notifications}*/}
          {/*                  onChange={(e) =>*/}
          {/*                    setNotifications({*/}
          {/*                      ...notifications,*/}
          {/*                      email_notifications: e.target.checked,*/}
          {/*                    })*/}
          {/*                  }*/}
          {/*                />*/}
          {/*                <label className="custom-control-label" htmlFor="emailNotifications">*/}
          {/*                  Email Notifications*/}
          {/*                </label>*/}
          {/*              </div>*/}
          {/*              <small className="form-text text-muted">*/}
          {/*                Receive notifications about events, orders, and important updates via email*/}
          {/*              </small>*/}
          {/*            </div>*/}

          {/*            <div className="form-group">*/}
          {/*              <div className="custom-control custom-switch">*/}
          {/*                <input*/}
          {/*                  type="checkbox"*/}
          {/*                  className="custom-control-input"*/}
          {/*                  id="pushNotifications"*/}
          {/*                  checked={notifications.push_notifications}*/}
          {/*                  onChange={(e) =>*/}
          {/*                    setNotifications({*/}
          {/*                      ...notifications,*/}
          {/*                      push_notifications: e.target.checked,*/}
          {/*                    })*/}
          {/*                  }*/}
          {/*                />*/}
          {/*                <label className="custom-control-label" htmlFor="pushNotifications">*/}
          {/*                  Push Notifications*/}
          {/*                </label>*/}
          {/*              </div>*/}
          {/*              <small className="form-text text-muted">*/}
          {/*                Receive real-time push notifications in the application*/}
          {/*              </small>*/}
          {/*            </div>*/}

          {/*            <button type="submit" className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600" disabled={isSaving}>*/}
          {/*              {isSaving ? 'Saving...' : 'Save Preferences'}*/}
          {/*            </button>*/}
          {/*          </form>*/}
          {/*        </div>*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*)}*/}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Change Password</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="form-group">
                        <label htmlFor="currentPassword">Current Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="currentPassword"
                          value={passwordData.current_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, current_password: e.target.value })
                          }
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="newPassword">New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="newPassword"
                          value={passwordData.new_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, new_password: e.target.value })
                          }
                          minLength={8}
                          required
                        />
                        <small className="form-text text-muted">
                          Password must be at least 8 characters long
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="confirmPassword"
                          value={passwordData.new_password_confirmation}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              new_password_confirmation: e.target.value,
                            })
                          }
                          minLength={8}
                          required
                        />
                      </div>

                      <button type="submit" className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600" disabled={isSaving}>
                        {isSaving ? 'Changing...' : 'Change Password'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Gateway Tab */}
          {activeTab === 'payment-gateway' && (
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">
                      <i className="fas fa-credit-card mr-2 text-purple-500"></i>
                      SADAD Payment Gateway
                    </h5>
                    {gateway.configured && (
                      <span className="badge badge-success d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                        <i className="fas fa-check-circle mr-1"></i> Connected
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    {isLoadingGateway ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
                      </div>
                    ) : (
                      <>
                        <div className="alert alert-info d-flex align-items-start mb-4" style={{ fontSize: '0.875rem' }}>
                          <i className="fas fa-info-circle mr-2 mt-1 flex-shrink-0"></i>
                          <div>
                            When configured, customer payments for your events will go through <strong>your SADAD account</strong> instead of the platform default.
                            Obtain your credentials from the <strong>SADAD Merchant Portal</strong>.
                          </div>
                        </div>

                        <form onSubmit={handleGatewaySubmit}>
                          <div className="form-group">
                            <label className="font-weight-bold">Environment</label>
                            <div className="d-flex gap-3 mt-1">
                              {['sandbox', 'production'].map((env) => (
                                <div key={env} className="custom-control custom-radio">
                                  <input
                                    type="radio"
                                    className="custom-control-input"
                                    id={`env_${env}`}
                                    name="environment"
                                    value={env}
                                    checked={gateway.environment === env}
                                    onChange={() => setGateway({ ...gateway, environment: env })}
                                  />
                                  <label className="custom-control-label text-capitalize" htmlFor={`env_${env}`}>
                                    {env}
                                    {env === 'sandbox' && <span className="text-muted small ml-1">(for testing)</span>}
                                    {env === 'production' && <span className="text-success small ml-1">(live payments)</span>}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="form-group">
                            <label htmlFor="sadad_merchant_id" className="font-weight-bold">Merchant ID <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              id="sadad_merchant_id"
                              value={gateway.merchant_id}
                              onChange={(e) => setGateway({ ...gateway, merchant_id: e.target.value })}
                              placeholder="Your SADAD Merchant ID"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="sadad_api_key" className="font-weight-bold">API Key <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              id="sadad_api_key"
                              value={gateway.api_key}
                              onChange={(e) => setGateway({ ...gateway, api_key: e.target.value })}
                              placeholder="Your SADAD API Key"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="sadad_api_secret" className="font-weight-bold">
                              API Secret <span className="text-danger">*</span>
                              {gateway.configured && (
                                <span className="text-muted font-weight-normal small ml-2">(leave as-is to keep existing)</span>
                              )}
                            </label>
                            <input
                              type="password"
                              className="form-control"
                              id="sadad_api_secret"
                              value={gateway.api_secret}
                              onChange={(e) => setGateway({ ...gateway, api_secret: e.target.value })}
                              placeholder={gateway.configured ? 'Stored securely — enter new value to change' : 'Your SADAD API Secret'}
                              required
                            />
                            <small className="form-text text-muted">Stored encrypted. Never displayed in full.</small>
                          </div>

                          <div className="d-flex align-items-center gap-3 mt-4">
                            <button
                              type="submit"
                              className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600"
                              disabled={isSavingGateway}
                            >
                              <i className={`fas ${isSavingGateway ? 'fa-spinner fa-spin' : 'fa-save'} mr-2`}></i>
                              {isSavingGateway ? 'Saving...' : gateway.configured ? 'Update Configuration' : 'Save Configuration'}
                            </button>

                            {gateway.configured && (
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={handleGatewayDelete}
                              >
                                <i className="fas fa-trash mr-2"></i>Remove
                              </button>
                            )}
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </div>

                {!gateway.configured && (
                  <div className="card mt-3 border-left-warning">
                    <div className="card-body py-3">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle text-warning mr-3 fa-lg"></i>
                        <div>
                          <strong>No gateway configured</strong>
                          <p className="mb-0 text-muted small">
                            Customer payments will use the platform's default payment gateway (SkipCash) until you configure SADAD.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bank-details' && (
            <div className="row mb-4">
              <div className="col-md-10">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Bank Details</h5>
                    <button
                      className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600"
                      onClick={() => setShowBankModal(true)}
                    >
                      <i className="fas fa-plus mr-2 "></i>Add New Bank Detail
                    </button>
                  </div>
                  <div className="card-body">
                    {isLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : bankDetails.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-university fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No bank details added yet</p>
                        <button
                          className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600"
                          onClick={() => setShowBankModal(true)}
                        >
                          <i className="fas fa-plus mr-2"></i>Add Your First Bank Detail
                        </button>
                      </div>
                    ) : (
                      <div className="bank-details-list">
                        {bankDetails.map((detail, index) => (
                          <div key={detail.id} className="card mb-3 border">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-3">
                                    <i className="fas fa-university text-purple-500 mr-2"></i>
                                    Bank Account #{index + 1}
                                  </h6>
                                  <div className="row">
                                      <div className="col-md-6 mb-2">
                                          <small className="text-muted d-block">Account Name</small>
                                          <strong>{detail.account_name}</strong>
                                      </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">IBAN</small>
                                      <strong>{detail.iban}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Account Number</small>
                                      <strong>{detail.bank_account_number}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Currency</small>
                                      <strong>{detail.bank_account_currency}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Added On</small>
                                      <strong>{new Date(detail.created_at).toLocaleDateString()}</strong>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-danger btn-sm ml-3"
                                  onClick={() => handleDeleteBankDetail(detail.id)}
                                  title="Delete bank detail"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Bank Detail Modal */}
      {showBankModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Bank Detail</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowBankModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleAddBankDetail}>
                <div className="modal-body">
                    <div className="form-group">
                        <label htmlFor="account_name">Account Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="account_name"
                            value={newBankDetail.account_name}
                            onChange={(e) => setNewBankDetail({ ...newBankDetail, account_name: e.target.value })}
                            placeholder="Enter Account Name"
                            required
                        />
                    </div>
                  <div className="form-group">
                    <label htmlFor="iban">IBAN *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="iban"
                      value={newBankDetail.iban}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, iban: e.target.value })}
                      placeholder="Enter IBAN"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bank_account_number">Bank Account Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="bank_account_number"
                      value={newBankDetail.bank_account_number}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, bank_account_number: e.target.value })}
                      placeholder="Enter account number"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bank_account_currency">Currency *</label>
                    <select
                      className="form-control"
                      id="bank_account_currency"
                      value={newBankDetail.bank_account_currency}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, bank_account_currency: e.target.value })}
                      required
                    >
                      <option value="QAR">QAR - Qatari Riyal</option>
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="SAR">SAR - Saudi Riyal</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="submit"
                    className="btn !bg-purple-500 !text-white !font-bold hover:!bg-purple-600"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Adding...' : 'Add Bank Detail'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerSettings;
